import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Button,
  Collapse,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { SelectOption } from "../../constants/SelectOption";
import useCustomToast from "../../lib/useCustomToast";
import { useFetchCategory } from "../../store/useFetchCategory";
import { useBorderColor } from "../../themes/colors";
import { CardSkeleton } from "../skeleton/CardSkeleton";

interface Props extends BoxProps {
  onSelected: (select: SelectOption | undefined) => void;
  onResetFilter: (reset: boolean) => void;
}

const CategoryFilter = ({ onSelected, onResetFilter, ...rest }: Props) => {
  const { data, error, isLoading, fetchCategory } = useFetchCategory();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectOption | undefined>(undefined);
  const borderColor = useBorderColor();
  const toast = useCustomToast();

  useEffect(() => {
    if (!data) {
      fetchCategory();
    }
  }, [fetchCategory, data]);

  useEffect(() => {
    if (error) {
      return toast({
        description: error,
      });
    }
  }, [error]);

  const handleToggle = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const handleSelect = (value: any) => {
    setSelected(value);
    value && onSelected(value);
  };

  const handleReset = () => {
    setSelected(undefined);
    setExpandedCategory(null);
    onResetFilter(true);
    toast({
      title: "Berhasil mereset filter",
      status: "success",
      duration: 2000,
    });
  };

  if (!isLoading && !data) {
    return;
  }

  return (
    <Box
      className="category-filter"
      p={4}
      borderRadius={"md"}
      borderWidth={"1px"}
      borderColor={borderColor}
      {...rest}
    >
      <Text textStyle={"b-reg-sb"} fontWeight={"semibold"}>
        Filter Kategori
      </Text>

      <VStack align="stretch" mt={2} gap={2}>
        {isLoading ? (
          <CardSkeleton w={"160px"} row={1} column={10} height={30} />
        ) : (
          data?.map((category) => (
            <Box key={category.name} width="full">
              <HStack
                fontSize={[10, null, 12]}
                fontWeight={
                  expandedCategory === category.name ? "semibold" : "normal"
                }
                justify={"space-between"}
                cursor={"pointer"}
                onClick={() => handleToggle(category.name)}
              >
                <Text>{category.name}</Text>

                {expandedCategory === category.name ? (
                  <ChevronUpIcon />
                ) : (
                  <ChevronDownIcon />
                )}
              </HStack>

              <Collapse in={expandedCategory === category.name}>
                <VStack w={"100%"} align={"stretch"} mt={2} pl={2} gap={0}>
                  {category.subCategories.map((item) => (
                    <Box
                      key={item.id}
                      as="button"
                      py={0.5}
                      textAlign={"start"}
                      onClick={() => handleSelect(item)}
                    >
                      <Text
                        fontSize={[10, null, 12]}
                        color={
                          selected && selected.name === item.name
                            ? "red.400"
                            : undefined
                        }
                        fontWeight={
                          selected && selected.name === item.name
                            ? "semibold"
                            : "normal"
                        }
                      >
                        {item.name}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Collapse>
            </Box>
          ))
        )}
      </VStack>

      <Button
        w={"full"}
        variant={"ghost"}
        colorScheme="red"
        mt={4}
        size={"xs"}
        onClick={handleReset}
      >
        Reset
      </Button>
    </Box>
  );
};

export default CategoryFilter;
